import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useCandidateDetail } from "@/hooks/candidate/useCandidateDetail";
import { usePageTitle } from "@/hooks/usePageTitle";
import { EditCandidateModal } from "@/components/candidates/EditCandidateModal";
import { Candidate } from "@/types/candidate";
import { useSettings } from "@/providers/SettingsProvider";
import { toast } from "@/hooks/use-toast";
import { CandidateDetailHeader } from "@/components/candidates/CandidateDetailHeader";
import { CandidateProfile } from "@/components/candidates/CandidateProfile";
import { CandidateTabs } from "@/components/candidates/CandidateTabs";
import { CandidateEmbeddingWrapper } from "@/components/candidates/CandidateEmbeddingWrapper";
import { AIAnalysisProgress } from "@/components/ui/AIAnalysisProgress";
import { DuplicateCandidatesBanner } from "@/components/ui/DuplicateCandidatesBanner";
import { useDuplicateCandidates } from "@/hooks/candidate/useDuplicateCandidates";
import { updateCandidate } from "@/utils/candidate/operations/updateCandidate";
import { LinkedInProfileModal } from "@/components/candidates/LinkedInProfileModal";
import { LinkedInCandidateMerger } from "@/components/candidates/LinkedInCandidateMerger";
import { supabase } from "@/integrations/supabase/client";
import { useTrackPageVisit } from "@/hooks/useEntityHistory";
import { useHasUnipileAccount } from "@/hooks/useHasUnipileAccount";

const CandidateDetail = () => {

    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();

    const {
        loading,
        candidate,
        cv,
        cvExists,
        comments,
        handleDelete,
        handleAddComment,
        handleUpdateComment,
        handleDeleteComment,
        handleUpdateCandidate,
        refetchCandidate
    } = useCandidateDetail(id);

    usePageTitle(candidate ? candidate.name : "Candidate");

	// Track page visit for history
	useTrackPageVisit('candidate', candidate?.id, candidate?.name, {
		candidateFunction: candidate?.function
	});

    const { devMode } = useSettings();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentCV, setCurrentCV] = useState<string | null>(null);    const [currentCVExists, setCurrentCVExists] = useState(cvExists);
    const [activeTab, setActiveTab] = useState("processes");
    const [isEditing, setIsEditing] = useState(false);
    const [editCandidate, setEditCandidate] = useState(candidate);
    const [kanbanRefreshKey, setKanbanRefreshKey] = useState(0);

    // Switch to tasks tab if task parameter is present
    useEffect(() => {
        const taskId = searchParams.get('task');
        if (taskId && activeTab !== 'tasks') {
            setActiveTab('tasks');
        }
    }, [searchParams]);

    // LinkedIn Profile Modal State
    const [isLinkedInModalOpen, setIsLinkedInModalOpen] = useState(false);
    const [linkedInProfileData, setLinkedInProfileData] = useState(null);
    const [linkedInLoading, setLinkedInLoading] = useState(false);
    const [linkedInError, setLinkedInError] = useState<string>('');
    
    // LinkedIn Candidate Merger State
    const [isLinkedInMergerOpen, setIsLinkedInMergerOpen] = useState(false);

    // Check for duplicate candidates
    const { duplicates, hasDuplicates } = useDuplicateCandidates(candidate);

    // Extract LinkedIn profile ID from LinkedIn URL
    const extractLinkedInId = (linkedinUrl: string): string | null => {
        if (!linkedinUrl) return null;
        
        try {
            const cleanUrl = linkedinUrl.split('?')[0];
            const pathParts = cleanUrl.split('/').filter((part: string) => part !== "");
            const linkedinId = pathParts.pop();
            if (linkedinId && typeof linkedinId === 'string') {
                return linkedinId.toLowerCase();
            }
        } catch (error) {
            // Silent error handling
        }
        return null;
    };

    // LinkedIn profile functionality
    // Handle update candidate with LinkedIn data
    const handleUpdateCandidateFromLinkedIn = () => {
        setIsLinkedInModalOpen(false); // Close the LinkedIn modal
        setIsLinkedInMergerOpen(true);  // Open the merger interface
    };

    // Handle saving merged candidate data
    const handleSaveMergedCandidate = async (mergedCandidate: Candidate) => {
        try {
            // Extract custom fields from the mergedCandidate if they exist
            const { custom_fields, ...regularFields } = mergedCandidate;
            
            // Call the update function
            const success = await updateCandidate(id!, regularFields, custom_fields || {});
            
            if (success) {
                toast({ 
                    title: "Success", 
                    description: "Candidate updated successfully with LinkedIn data" 
                });
                
                // Refetch candidate data to show updated information
                if (refetchCandidate) {
                    await refetchCandidate();
                }
            } else {
                throw new Error('Failed to update candidate');
            }
        } catch (error) {
            console.error('Error updating candidate with LinkedIn data:', error);
            throw error; // Re-throw to let the merger component handle the error display
        }
    };

    const linkedinId = extractLinkedInId(candidate?.linkedin);
    const showLinkedInButton = !!linkedinId;

    // Check whether current user has an active Unipile account via edge function
    const { hasUnipileAccount } = useHasUnipileAccount();

    const canShowLinkedInButton = showLinkedInButton && hasUnipileAccount;

    const handleGetLinkedInProfile = async () => {
        if (!linkedinId) return;

        setLinkedInLoading(true);
        setLinkedInError('');
        setIsLinkedInModalOpen(true);

        try {
            const { data, error } = await supabase.functions.invoke('unipile-handler?command=get-linkedin-profile&participant_id=' + linkedinId, {
                method: 'GET'
            });

            if (error) {
                throw new Error(`Edge function error: ${error.message}`);
            }

            if (!data) {
                throw new Error('No data returned from edge function');
            }

            setLinkedInProfileData(data);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load LinkedIn profile';
            setLinkedInError(errorMessage);
            toast({
                title: "Error",
                description: errorMessage,
                variant: "destructive"
            });
        } finally {
            setLinkedInLoading(false);
        }
    };

    // Update CV state when the data changes
    useEffect(() => {
        if (cv !== currentCV) {
            setCurrentCV(cv);
        }

        if (cvExists !== currentCVExists) {
            setCurrentCVExists(cvExists);
        }
    }, [cv, cvExists]);

    const handleEdit = () => {
        setEditCandidate(candidate);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditCandidate(candidate);
    };

    const handleSave = async () => {
        // Only save if there are actual changes
        const hasChanges = JSON.stringify(editCandidate) !== JSON.stringify(candidate);
        if (!hasChanges) {
            setIsEditing(false);
            toast({ title: "No changes", description: "No changes to save." });
            return;
        }
        
        // Immediately close edit mode and show success toast for better UX
        setIsEditing(false);
        toast({ title: "Success", description: "Candidate updated successfully" });
        
        try {
            // Extract custom fields from the editCandidate
            const { custom_fields, ...regularFields } = editCandidate;
            
            // Call the update function directly (same as companies)
            const success = await updateCandidate(id!, regularFields, custom_fields || {});
            
            if (success) {
                // Refetch in background to ensure data consistency
                if (refetchCandidate) {
                    refetchCandidate().catch(error => {
                        console.error("Background refetch failed:", error);
                    });
                }
            } else {
                // If save failed, revert to edit mode
                setIsEditing(true);
                toast({ title: "Error", description: "Failed to update candidate", variant: "destructive" });
            }
        } catch (error) {
            // If save failed, revert to edit mode
            setIsEditing(true);
            toast({ title: "Error", description: "Failed to update candidate", variant: "destructive" });
        }
    };

    const handleCVUpdate = (newCVUrl: string) => {
        setCurrentCV(newCVUrl);
        setCurrentCVExists(true);
    };

    const handleLocalDelete = async () => {
        if (id) {
            try {
                await handleDelete();
            } catch (error) {
                console.error("Error during delete process:", error);
                toast({
                    title: "Error",
                    description: "Failed to delete candidate",
                    variant: "destructive",
                });
            }
        }
    };

    if (loading) {
        return (
            <div className="animate-pulse p-6">
                <div className="h-10 bg-muted rounded-none mb-4"></div>
                <div className="space-y-3">
                    <div className="h-24 bg-muted rounded-none"></div>
                    <div className="h-48 bg-muted rounded-none"></div>
                </div>
            </div>
        );
    }

    if (!candidate) {
        return (
            <div className="p-6">
                <div className="border border-border rounded-none p-6 text-center">
                    <h2 className="text-lg font-medium mb-1">Candidate Not Found</h2>
                    <p className="text-sm text-muted-foreground">The candidate you are looking for does not exist or has been deleted.</p>
                </div>
            </div>
        );
    }

    return (        
    
    <div className="w-full p-0 m-0">

            {/* Duplicate candidates banner - show above AIAnalysisProgress */}            
            {hasDuplicates && candidate && (
                <DuplicateCandidatesBanner
                    candidate={candidate}
                    duplicates={duplicates}
                    onMergeComplete={() => {
                        // Merge completed - no automatic refresh
                    }}
                />
            )}

            <AIAnalysisProgress
                entityId={candidate.id}
                entityType="candidate"
                onRefresh={() => {
                    if (refetchCandidate) {
                        refetchCandidate();
                    }
                }}
            />


            <CandidateDetailHeader
                isEditing={isEditing}
                onEdit={handleEdit}
                onSave={handleSave}
                onCancel={handleCancel}
                handleDelete={handleLocalDelete}
                candidateName={candidate?.name || ''}
                candidateId={candidate?.id || ''}
                linkedinId={linkedinId}
                showLinkedInButton={canShowLinkedInButton}
                onGetLinkedInProfile={handleGetLinkedInProfile}
            />

            {/* Ensure no space above CandidateProfile sticky banner */}
            <div className="p-0 m-0">
              <CandidateProfile
                  candidate={isEditing ? editCandidate : candidate}
                  isEditing={isEditing}
                  onChange={setEditCandidate}
                  onCandidateUpdate={handleUpdateCandidate}
                  onStatusChanged={() => setKanbanRefreshKey(k => k + 1)}
                  cv={currentCV}
                  cvExists={currentCVExists}
                  onCVUpdate={handleCVUpdate}
                  onSave={handleSave}
                  onCancel={handleCancel}
              />
            </div>

            <CandidateEmbeddingWrapper
                candidate={candidate}
                devMode={devMode}
                onUpdate={() => refetchCandidate()}
            />

            <CandidateTabs
                candidateId={candidate.id}
                comments={comments}
                cv={currentCV}
                candidate={candidate}
                handleAddComment={handleAddComment}
                handleUpdateComment={handleUpdateComment}
                handleDeleteComment={handleDeleteComment}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                refetchCandidate={refetchCandidate}
                kanbanRefreshKey={kanbanRefreshKey}
            />

            {/* LinkedIn Profile Modal */}
            <LinkedInProfileModal
                isOpen={isLinkedInModalOpen}
                onClose={() => {
                    setIsLinkedInModalOpen(false);
                    setLinkedInProfileData(null);
                    setLinkedInError('');
                }}
                profileData={linkedInProfileData}
                loading={linkedInLoading}
                error={linkedInError}
                onUpdateCandidate={handleUpdateCandidateFromLinkedIn}
                showUpdateButton={!linkedInLoading && !linkedInError && !!linkedInProfileData}
                candidateExists={true} // We're on a candidate detail page, so the candidate exists
            />

            {/* LinkedIn Candidate Merger */}
            {isLinkedInMergerOpen && candidate && linkedInProfileData && (
                <LinkedInCandidateMerger
                    isOpen={isLinkedInMergerOpen}
                    onClose={() => setIsLinkedInMergerOpen(false)}
                    candidate={candidate}
                    linkedInProfile={linkedInProfileData}
                    onSave={handleSaveMergedCandidate}
                />
            )}
        </div>
    );
};

export default CandidateDetail;
